/**
 * src/components/spatial/mapClickClaim.ts
 *
 * Tiny coordination module that lets one part of the app (the spatial
 * admin overlay) tell another part (App.tsx's map-click handlers) to
 * stand down for a beat.
 *
 * Why this exists
 * ---------------
 * Mapbox dispatches a single click event to every registered listener,
 * including `map.on('click', 'countries-fill', handleCountryClick)`.
 * When the operator activates "Import 3D building" or "Draw geometry"
 * mode in the spatial admin overlay, both handlers fire and the
 * country tooltip pops up on top of the building they were trying to
 * import.
 *
 * The admin overlay calls `claimMapClicks('import-building')` (or
 * `'draw'`) when it enters one of those modes, and the country
 * handlers in App.tsx check `isMapClicksClaimed()` early and bail.
 *
 * Modes are advisory; the only thing that matters externally is "is
 * something currently claiming map clicks?" — kept as `currentClaim`.
 */

export type MapClickClaimMode = 'import-building' | 'draw' | 'pick-point' | 'pick-parent';

let currentClaim: MapClickClaimMode | null = null;

/**
 * Mark map clicks as claimed by the given mode. Subsequent
 * `isMapClicksClaimed()` calls return true until {@link releaseMapClicks}
 * runs. Calling this while a different claim is active overwrites the
 * previous one (the old one is presumed to have been torn down by
 * whoever set it).
 */
export function claimMapClicks(mode: MapClickClaimMode): void {
  currentClaim = mode;
}

/** Release the active claim. Safe to call when nothing is claimed. */
export function releaseMapClicks(): void {
  currentClaim = null;
}

/** True if some module is actively waiting for the next map click. */
export function isMapClicksClaimed(): boolean {
  return currentClaim != null;
}

/** Inspect the active claim (mainly for diagnostics / logging). */
export function getMapClickClaim(): MapClickClaimMode | null {
  return currentClaim;
}
